// Centered lightbox modal for moodboard / canvas tile previews.
// Closes on Escape, outside click, or the close button. Used by BrandPage's
// product/lifestyle tiles so the demo feels interactive even without a
// real asset detail backend.

import { X } from "lucide-react";
import { useEffect } from "react";

import { cn } from "@/lib/utils";

interface LightboxProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  thumb: string;
  meta?: { label: string; value: string }[];
}

export function Lightbox({ open, onClose, title, subtitle, thumb, meta = [] }: LightboxProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      className={cn(
        "fixed inset-0 z-[200] flex items-center justify-center p-6",
        "bg-background/80 backdrop-blur-sm animate-fade-in"
      )}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-3xl rounded-2xl bg-card border border-border shadow-card-hover overflow-hidden"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 h-9 w-9 rounded-full bg-background/80 backdrop-blur-sm border border-border flex items-center justify-center hover:bg-background transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="aspect-[16/10] w-full" style={{ background: thumb }} />
        <div className="p-6 space-y-4">
          <div>
            {subtitle && (
              <p className="text-tiny tracking-[0.18em] uppercase text-muted-foreground">
                {subtitle}
              </p>
            )}
            <h2 className="font-freight text-3xl font-light tracking-tight leading-tight mt-1">
              {title}
            </h2>
          </div>
          {meta.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-3 border-t border-border">
              {meta.map((m) => (
                <div key={m.label}>
                  <p className="text-tiny tracking-[0.18em] uppercase text-muted-foreground">
                    {m.label}
                  </p>
                  <p className="text-sm mt-0.5">{m.value}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
